import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { BrandQuery, Dictionary, EntitiesModel, ModelQuery, SerieQuery, SerieResponse } from 'app/models/entities.model';
import { Piece, PieceType, PrintableData } from 'app/models/printable-data.model';
import { groupBy } from 'lodash';
import { Observable, forkJoin, map } from 'rxjs';
import { Brand } from '../models/brand.model';
import { Model } from '../models/model.model';
import { Serie } from '../models/serie.model';
import { parseApiDecimal } from '../utils/api-decimal.utils';

type ApiPiece = Omit<Piece, 'height' | 'width'> & {
  height: string;
  width: string;
};

type ApiPrintableData = Omit<PrintableData, 'pieces'> & {
  pieces?: ApiPiece[];
};

@Injectable({
  providedIn: 'root',
})
export class PrintableDataService {
  private _http = inject(HttpClient);

  loadEntities(): Observable<EntitiesModel> {
    return forkJoin({
      brands: this.loadBrands(),
      series: this.loadSeries(),
      models: this.loadModels(),
      printableData: this.loadAllPrintableData(),
    }).pipe(
      map(entities => {
        const ppfPrintableData = entities.printableData.filter(printableData => printableData.type === PieceType.PPF);
        const vitrePrintableData = entities.printableData.filter(printableData => printableData.type === PieceType.WINDOW);

        const ppfModels = entities.models.filter(model => ppfPrintableData.map(printableData => printableData.modelId).includes(model.id));
        const vitreModels = entities.models.filter(model => vitrePrintableData.map(printableData => printableData.modelId).includes(model.id));

        const ppfSeries = entities.series.filter(serie => ppfModels.map(model => model.serieId).includes(serie.id));
        const vitreSeries = entities.series.filter(serie => vitreModels.map(model => model.serieId).includes(serie.id));

        const groupedBrands = {
          [PieceType.PPF]: entities.brands.filter(brand => ppfSeries.map(serie => serie.brandId).includes(brand.id)).map(brand => brand.id),
          [PieceType.WINDOW]: entities.brands.filter(brand => vitreSeries.map(serie => serie.brandId).includes(brand.id)).map(brand => brand.id),
          [PieceType.ALL]: entities.brands.map(brand => brand.id),
        };

        return {
          ...entities,
          groupedBrands,
          groupedSeries: {
            [PieceType.PPF]: this.groupEntityBy(ppfSeries, 'brandId'),
            [PieceType.WINDOW]: this.groupEntityBy(vitreSeries, 'brandId'),
            [PieceType.ALL]: this.groupEntityBy(entities.series, 'brandId'),
          },
          groupedModels: {
            [PieceType.PPF]: this.groupEntityBy(ppfModels, 'serieId'),
            [PieceType.WINDOW]: this.groupEntityBy(vitreModels, 'serieId'),
            [PieceType.ALL]: this.groupEntityBy(entities.models, 'serieId'),
          },
          groupedPrintableData: {
            [PieceType.PPF]: this.groupEntityBy(ppfPrintableData, 'modelId'),
            [PieceType.WINDOW]: this.groupEntityBy(vitrePrintableData, 'modelId'),
            [PieceType.ALL]: this.groupEntityBy(entities.printableData, 'modelId'),
          },
        };
      }),
    );
  }

  private groupEntityBy<T extends Serie | Model | PrintableData, Y extends keyof T>(entity: T[], id: Y): Dictionary<number[]> {
    const groupedEntity = groupBy(entity, id);
    const newGroupedEntity: Dictionary<number[]> = {};

    Object.keys(groupedEntity).forEach(key => {
      newGroupedEntity[key] = groupedEntity[key].map(entity => entity.id);
    });

    return newGroupedEntity;
  }

  loadBrands(): Observable<Brand[]> {
    const url = '/api/brand/list';

    return this._http.get<{ statusCode: number; data: { listing: Brand[]; total: number } }>(url).pipe(map(response => response.data.listing));
  }

  getBrandList(query: BrandQuery = {}): Observable<{ statusCode: number; data: { listing: Brand[]; total: number } }> {
    const routeParams: Record<keyof BrandQuery, string> = {
      ids: 'ids',
      exclude: 'exclude',
      brandName: 'brand-name',
      exactSearch: 'exact-search',
      offset: 'offset',
      limit: 'limit',
    };
    const params = this.buildQueryParams(query, routeParams);

    return this._http.get('/api/brand/list', { observe: 'response', params }).pipe(
      map((res: HttpResponse<{ data: { listing: Brand[]; total: number }; statusCode: number }>) => {
        return res.body;
      }),
    );
  }

  loadSeries(): Observable<Serie[]> {
    const url = '/api/serie/list';

    return this._http.get<{ statusCode: number; data: { listing: Serie[]; total: number } }>(url).pipe(map(response => response.data.listing));
  }

  getSerieList(query: SerieQuery = {}): Observable<SerieResponse> {
    const routeParams: Record<keyof SerieQuery, string> = {
      ids: 'ids',
      exclude: 'exclude',
      brandName: 'brand-name',
      serieName: 'serie-name',
      offset: 'offset',
      limit: 'limit',
      showParents: 'show-parents',
      brandIds: 'brand-ids',
      exactSearch: 'exact-search',
    };
    const params = this.buildQueryParams(query, routeParams);

    return this._http.get('/api/serie/list', { observe: 'response', params }).pipe(
      map((res: HttpResponse<SerieResponse>) => {
        return res.body;
      }),
    );
  }

  loadModels(): Observable<Model[]> {
    const url = '/api/model/list';

    return this._http.get<{ statusCode: number; data: { listing: Model[]; total: number } }>(url).pipe(map(response => response.data.listing));
  }

  getModelList(query: ModelQuery = {}): Observable<{ statusCode: number; data: { listing: Model[]; total: number } }> {
    const route = {
      brandName: 'brand-name',
      serieName: 'serie-name',
      modelName: 'model-name',
      offset: 'offset',
      limit: 'limit',
      showParents: 'show-parents',
      serieIds: 'serie-ids',
      exclude: 'exclude',
      ids: 'ids',
      exactSearch: 'exact-search',
    };
    const params = this.buildQueryParams(query, route);

    return this._http.get('/api/model/list', { observe: 'response', params }).pipe(
      map((res: HttpResponse<{ data: { listing: Model[]; total: number }; statusCode: number }>) => {
        return res.body;
      }),
    );
  }

  getModel(modelId: number): Observable<{ statusCode: number; data: Model }> {
    return this._http.get('/api/model/' + modelId, { observe: 'response' }).pipe(
      map((res: HttpResponse<{ data: Model; statusCode: number }>) => {
        return res.body;
      }),
    );
  }

  loadAllPrintableData(): Observable<PrintableData[]> {
    const url = '/api/printable-data/list';

    return this._http
      .get<{ statusCode: number; data: { listing: ApiPrintableData[]; total: number } }>(url)
      .pipe(map(response => response.data.listing.map(printableData => this.normalizePrintableData(printableData))));
  }

  loadPrintableData(id: number): Observable<PrintableData> {
    const url = `/api/printable-data/${id}`;

    return this._http.get<{ statusCode: number; data: ApiPrintableData }>(url).pipe(map(response => this.normalizePrintableData(response.data)));
  }

  private normalizePrintableData(printableData: ApiPrintableData): PrintableData {
    return {
      ...printableData,
      pieces: printableData.pieces?.map(piece => ({
        ...piece,
        height: parseApiDecimal(piece.height),
        width: parseApiDecimal(piece.width),
      })),
    };
  }

  private buildQueryParams<Query extends object>(query: Query, route: Record<keyof Query, string>): HttpParams {
    let params = new HttpParams();

    for (const key of Object.keys(query) as (keyof Query)[]) {
      const value = query[key];
      if (value == null) {
        continue;
      }

      if (Array.isArray(value)) {
        value.forEach((item, index) => {
          params = params.append(`${route[key]}[${index}]`, String(item));
        });
      } else {
        params = params.set(route[key], String(value));
      }
    }

    return params;
  }
}
